// @ts-check
/* eslint-disable comma-dangle,no-console */
import React from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Spinner from "../../../qwc2/components/widgets/Spinner";
import LocaleUtils from "../../../qwc2/utils/LocaleUtils";
import FeatureUtils from "../../utils/FeatureUtils";
import AbstractTab from "./AbstractTab";
import {connect} from "react-redux";
import {addDetailWindow, removeDetailWindow, setDetailWindow} from "../../actions/details";
import AjaxTableTab from "./AjaxTableTab";
import cloneDeep from "lodash/cloneDeep";
import FormTab from "./FormTab";

/**
 * @typedef {import('./DocumentsTab').DocumentsTab} DocumentsTabType
 * @typedef {import('./DocumentsTab').DocumentsTabProps} DocumentsTabProps
 * @typedef {import('./DocumentsTab').DocumentsTabState} DocumentsTabState
 * @typedef {import('./DocumentsTab').TabObject} TabObject
 * @typedef {import('./DocumentsTab').FeatureObject} FeatureObject
 */
/**
 * @extends {React.Component<DocumentsTabProps, DocumentsTabState>}
 * @public props { DocumentsTabProps }
 * @public state { DocumentsTabState }
 *
 **/
class DocumentsTab extends AbstractTab {
    static propTypes = {
        addDetailWindow: PropTypes.func,
        feature: PropTypes.object,
        removeDetailWindow: PropTypes.func,
        setDetailWindow: PropTypes.func,
        tab: PropTypes.object,
        windowId: PropTypes.string,
        windowType: PropTypes.string
    };

    /** @type { Partial<DocumentsTabProps> }*/
    static defaultProps = {
        feature: {},
        tab: {},
        windowId: "",
    };

    /** @param props { DocumentsTabProps } */
    constructor(props) {
        super(props);
        /** @type  { DocumentsTabState } */
        this.state = {
            isLoading: true,
            error: null,  /** string */
            types: [],
            selectedType: "",
            uploads: [], // {file, progress, status, error}
            isDragHover: false,
        };
    }

    componentDidMount() {
        this.loadTypes();
    }

    /** @this { DocumentsTabType } */
    async loadTypes() {
        const {tab, feature} = this.props;
        const url = FeatureUtils.prepareUrl(feature, tab.config.typesUrl);
        try {
            const response = await axios.get(url);
            const types = (response.data.features || [])
                .filter(f => f.type === "Feature")
                .map(f => ({
                    label: f.properties.name || f.properties.description,
                    value: f.properties.id
                }));
            this.setState({types, isLoading: false});
        } catch (err) {
            console.error("Error loading types:", err);
            this.setState({error: err, isLoading: false});
        }
    }

    /** @this { DocumentsTabType } */
    handleTypeChange = (ev) => {
        this.setState({selectedType: ev.target.value});
    };

    /** @this { DocumentsTabType } */
    handleFilesHover = (files) => {
        if (!this.state.selectedType) {
            return;
        }
        this.setState({ isDragHover: true });
    };

    /** @this { DocumentsTabType } */
    handleDragOver = (e) => {
        if (!this.state.selectedType) {
            return;
        }
        e.preventDefault();
        if (!this.state.isDragHover) {
            this.setState({ isDragHover: true });
        }
    };

    /** @this { DocumentsTabType } */
    handleDragLeave = (e) => {
        if (!this.state.selectedType) {
            return;
        }
        // prevent flicker when hovering child elements
        this.setState({ isDragHover: false });
    };

    /** @this { DocumentsTabType } */
    handleDrop = (e) => {
        if (!this.state.selectedType) {
            return;
        }
        e.preventDefault();
        this.setState({ isDragHover: false });
        this.handleFiles(e.dataTransfer.files);
    };

    /**
     * Uploads one or more files (dropped or selected)
     * @this { DocumentsTabType }
     * @param files { FileList }
     * */
    handleFiles = (files) => {
        if (!files) {
            return;
        }
        const {
            tab,
            feature,
        } = /** @type { DocumentsTabProps } */ this.props;
        const {
            selectedType,
        } = /** @type { DocumentsTabState } */ this.state;
        const {
            config,
            url,
        } = tab;
        const postDataFromConfig = cloneDeep(config.data);
        const method = this.props.tab.config.method || 'post';

        const sendAction = function(finalUrl, sendData, requestConfig) {
            switch (method.toLowerCase()) {
                case 'post':
                    return axios.post(finalUrl, sendData, requestConfig);
                case 'patch':
                    return axios.patch(finalUrl, sendData, requestConfig);
                default:
                    throw new Error(`Unsupported method ${method}`);
            }
        };

        Array.from(files).forEach((file) => {
            console.log('Preparing send for file', { file });
            /** @type UploadItem */
            const uploadItem = {
                file,
                progress: 0,
                status: "pending",
                error: null,
                hide: false,
                createdAt: new Date(),
                updatedAt: new Date(),
            };
            this.setState(prev => ({uploads: [...prev.uploads, uploadItem]}));

            /** @param update { Partial<UploadItem> } */
            const updateUpload = (update) => {
                this.setState(prev => ({
                    uploads: prev.uploads.map(u =>
                        u.file !== file ? u : {...u, updatedAt: new Date(), ...update}
                    )
                }));
            };
            /**
             * Auto-remove successful uploads after X seconds
             * @param seconds {number}
             **/
            const scheduleHideUpload = (seconds) => {
                setTimeout(() => {
                    updateUpload({ hide: true });
                }, seconds * 1000);
            };

            const fieldData = {
                field: {
                    name: file.name,
                    type: selectedType,
                    file: file,
                },
            };
            const sendData = new FormData();

            Object.keys(postDataFromConfig).forEach(k => {
                let v = postDataFromConfig[k];
                if (v === '{field.file}') {
                    // In FormTab.jsx that is: `if(filePlaceholders[v])` with `const filePlaceholders = {'{field.file}': 'file'}` -> `dataFileKey = 'file'` -> `file = fieldData.field[dataFileKey]`
                    // but, as there's no config for the fields, we don't need to look it up.
                    sendData.append(k, file, file.name);
                } else {
                    if (typeof v === 'string') {
                        v = FeatureUtils.prepareUrl(feature, v);
                        v = FeatureUtils.genericResolve(fieldData, v);
                    }
                    sendData.append(k, v);
                }
            });
            const finalUrl = FeatureUtils.prepareUrl(feature, url);
            const requestConfig = {
                headers: {},
                onUploadProgress: (evt) => {
                    const percent = Math.round((evt.loaded * 100) / evt.total);
                    updateUpload({ status: "uploading", progress: percent });
                },
            };
            if (config.headers) {
                Object.keys(config.headers).forEach((k) => {
                    requestConfig.headers[k] = FeatureUtils.prepareUrl(feature, config.headers[k]);
                });
            }

            sendAction(finalUrl, sendData, requestConfig)
                .then((res) => {
                    updateUpload({ progress: 100 });
                    if (typeof res === 'undefined') {
                        updateUpload({ status: "error", error: 'No server response data.' });
                        throw new Error('No response data!');
                    }
                    updateUpload({ status: "done", successAt: new Date() });
                    scheduleHideUpload(1.0);
                })
                .catch((error) => {
                    console.error('Error sending', error);
                    updateUpload({ status: "error", error: error });
                })
            ;
        });
    };

    /** @this { DocumentsTabType } */
    render() {
        const {tab, feature, windowId, windowType} = this.props;
        const {
            isLoading,
            types,
            selectedType,
            uploads,
            error,
            isDragHover,
        } = this.state;
        const config = tab.config || {};

        if (isLoading) {
            return (
                <div className="feature-details-fileupload">
                    <Spinner/>
                    <div>{LocaleUtils.tr("attribtable.loading")}</div>
                </div>
            );
        } else if (error !== null) {
            return (
                <div className="feature-details-fileupload">
                    <div>{ LocaleUtils.tr("attribtable.loadfailed") }</div>
                    <div>{ error }</div>
                </div>
            );
        }

        /* ajax table cache breaker */
        const lastSuccess = uploads.reduce((prev, curr) => Math.max(curr.successAt?.getTime() ?? 0, prev), 0);
        const shownUploads = uploads.filter((upload) => !upload.hide);

        /** @type { FeatureObject } */
        const futurePatched = {
            ...feature,
            type: selectedType,
        };
        const ajaxTableUrl = FeatureUtils.prepareUrl(futurePatched, config.listUrl);
        const downloadUrl = tab.url.replace('/upload', '/download?docId={uuid}&token={token}');
        const deleteUrl = tab.url.replace('/upload', '/{uuid}');

        let tableColumns = [
            {
                key: "name",
                label: "Name"
            },
            {
                key: "documentType",
                label: "Kategorie",
                type: "linktitle",
                objectType: "documenttype"
            },
            {
                key: "description",
                label: "Beschreibung"
            },
            {
                key: "extension",
                label: "Dateityp"
            },
            {
                key: "actions",
                label: "",
                type: "actions",
                elements: [
                    {
                        type: "download",
                        url: downloadUrl,
                        icon: "icon-arrow-down"
                    },
                    {
                        type: "delete",
                        url: deleteUrl,
                        icon: "icon-trash",
                        confirm: "Do you really want to delete this document?"
                    }
                ],
            },
        ];
        if (config.table && config.table.columns) {
            tableColumns = config.table.columns;
        }

        return (
            <div
                className={`feature-details-fileupload ${isDragHover ? "drag-hover" : ""}`}
                onDragOver={this.handleDragOver}
                onDragLeave={this.handleDragLeave}
                onDrop={this.handleDrop}
            >
                {/* Dropdown styled like FormTab */}
                <div className="feature-details-field">
                    <label>{config.selectMessage || "Bitte Typ auswählen"}</label>
                    <div className="feature-details-input">
                        <select
                            onChange={this.handleTypeChange}
                            value={selectedType || ""}
                        >
                            <option value="">-</option>
                            {types.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Drag & Drop area */}
                {selectedType && (
                    <div
                        className="feature-details-field upload-drop-area"
                        onClick={() => this.fileInput.click()}
                    >
                        {config.uploadInfo || "Dateien hier ablegen oder klicken"}
                        <input
                            type="file"
                            multiple
                            ref={ref => (this.fileInput = ref)}
                            style={{display: "none"}}
                            onChange={e => this.handleFiles(e.target.files)}
                        />
                    </div>
                )}

                {/* Upload progress */}
                {shownUploads.length > 0 && (
                    <div className="feature-details-field">
                        <label>Uploads</label>
                        <div className="feature-details-input feature-details-documents-upload-progresses">
                            {shownUploads.map((u, idx) => (
                                <div className="feature-details-documents-upload-progress" key={idx}>
                                    <div><strong>{u.file.name}</strong></div>
                                    <div className="feature-details-documents-upload-progress-bar">
                                        <div
                                            className={`feature-details-documents-upload-progress-fill ${u.status === "error" ? "error" : ""}`}
                                            style={{ width: `${u.progress}%` }}
                                        />
                                    </div>
                                    {u.error && (
                                        <div className="feature-details-documents-upload-error">
                                            <strong>{u.error.toString()}</strong>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <div className="display-none">Last success: { lastSuccess.toString() }</div>
                {/* Completed uploads list */}
                {selectedType && (
                    <AjaxTableTab
                        feature={feature}
                        key={`${selectedType}|${lastSuccess}`}
                        tab={{
                            ...tab,
                            url: ajaxTableUrl,
                            config: {
                                columns: tableColumns
                            }
                        }}
                        windowId={windowId}
                        windowType={windowType}
                    />
                )}
                <div className="display-none">
                    <pre>{JSON.stringify(this.props, null, 2)}</pre>
                </div>
            </div>
        );
    }
}

const selector = () => ({});

export default connect(selector, {
    setDetailWindow,
    addDetailWindow,
    removeDetailWindow,
})(DocumentsTab);
